import { useMemo, useState } from "react";
import { CustomerModel } from "@/models/pipeline";
import { UnderwritingQueueClaim } from "@/services/UnderwritingService";
import { Spinner } from "@/components/ui/spinner";
import { queueCustomerAge } from "@/helpers/underwritingQueueDashboard";
import { UnderwritingQueueAccordion } from "./UnderwritingQueueAccordion";
import { UnderwritingQueueCustomerRow } from "./UnderwritingQueueCustomerRow";

type UnderwritingQueueTableProps = {
  customers: CustomerModel[];
  /** Claim status per application, keyed by applicationId — unclaimed apps simply have no entry. */
  claimsByApplicationId?: Map<number, UnderwritingQueueClaim>;
  currentUserName?: string | null;
  isLoading?: boolean;
  onRowClick: (application: CustomerModel) => void;
  onClaim?: (customer: CustomerModel) => Promise<void>;
};

type AgeSortDirection = "asc" | "desc" | null;

/**
 * Underwriting Queue list with queue-specific columns and styling.
 */
export function UnderwritingQueueTable({ customers, claimsByApplicationId, currentUserName, isLoading, onRowClick, onClaim }: UnderwritingQueueTableProps) {
  const [ageSortDirection, setAgeSortDirection] = useState<AgeSortDirection>("desc");

  const sortedCustomers = useMemo(
    () => {
      if (ageSortDirection === null) return customers;
      const now = Date.now();
      return [...customers].sort((first, second) => {
        const firstAge = queueCustomerAge(first, now);
        const secondAge = queueCustomerAge(second, now);
        if (firstAge === null) return secondAge === null ? 0 : 1;
        if (secondAge === null) return -1;
        return ageSortDirection === "asc" ? firstAge - secondAge : secondAge - firstAge;
      });
    },
    [customers, ageSortDirection]
  );

  const cycleAgeSort = () => {
    setAgeSortDirection((prev) => (prev === "desc" ? "asc" : prev === "asc" ? null : "desc"));
  };

  return (
    <UnderwritingQueueAccordion
      count={sortedCustomers.length}
      ageSortDirection={ageSortDirection}
      onToggleAgeSort={cycleAgeSort}
    >
      {sortedCustomers.length === 0 && (
        <tr>
          <td colSpan={5} className="px-4 py-8 text-center text-[11px] text-muted-foreground">
            {isLoading ? (
              <span className="inline-flex items-center gap-2">
                <Spinner /> Loading applications…
              </span>
            ) : (
              "No applications found."
            )}
          </td>
        </tr>
      )}

      {sortedCustomers.map((customer) => (
        <UnderwritingQueueCustomerRow
          key={customer.applicationId}
          customer={customer}
          claim={claimsByApplicationId?.get(customer.applicationId)}
          currentUserName={currentUserName}
          onClick={onRowClick}
          onClaim={onClaim}
        />
      ))}
    </UnderwritingQueueAccordion>
  );
}
